// Portal › Kullanıcılar › "Yöneticiler" sekmesi (yalnız Süper Admin — Admin oturumunda DOM'da yok,
// portal yetki v2 §8.2). Yönetici tablosu (Rol, kapsam özeti, durum) + "Yönetici ata" + satırda
// "Rol ve kapsam" / "Yöneticiliği kaldır". Son aktif Süper Admin kaldırılamaz/düşürülemez (409
// CLIENT_ADMIN_LAST; düğme neden metniyle pasif). Veri üst bileşende (ClientUsers) yüklenir.

import { useState } from "react";
import {
  CLIENT_ADMIN_ROLE_LABELS,
  type ClientAdminRow,
  type ClientAdminSet,
  type ClientEntity,
} from "@mahir/shared";
import {
  grantPortalClientAdmin,
  revokePortalClientAdmin,
  updatePortalClientAdmin,
} from "../../api/client-admin";
import { CLIENT_ADMIN_COPY as C, CLIENT_ADMINS_CARD_COPY as K } from "../../lib/client-admin-copy";
import { scopeSummaryParts } from "../../lib/client-access-draft-v2";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { AdminGrantModal, type AdminFormValue } from "./AdminGrantModal";

interface Props {
  clientId: string;
  admins: ClientAdminRow[] | null;
  loadError: boolean;
  sets: ClientAdminSet[];
  entities: ClientEntity[];
  /** `/sets` yüklemesi sonucu: failed iken Admin kapsamı bilinmeden yazılamaz. */
  scopeStatus: "loading" | "ready" | "failed";
  onRetryScope: () => void;
  /** Değişiklikten sonra yönetici + kullanıcı listelerini yeniden yükle. */
  onChanged: () => Promise<void>;
}

export function ClientAdminsPanel(props: Props): JSX.Element {
  const { clientId, admins, loadError, sets, entities, onChanged } = props;
  const [grantOpen, setGrantOpen] = useState(false);
  const [editRow, setEditRow] = useState<ClientAdminRow | null>(null);
  const [removeRow, setRemoveRow] = useState<ClientAdminRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const activeSupers = (admins ?? []).filter((a) => a.role === "super_admin" && a.isActive);
  const isLastSuper = (a: ClientAdminRow): boolean =>
    a.role === "super_admin" && a.isActive && activeSupers.length <= 1;

  async function handleSubmit(v: AdminFormValue): Promise<void> {
    if (editRow) {
      await updatePortalClientAdmin(clientId, editRow.userId, {
        role: v.role,
        ...(v.role === "admin" && v.scope !== undefined ? { scope: v.scope } : {}),
      });
      setEditRow(null);
      setNotice({ kind: "ok", text: C.admins.updated });
    } else {
      const res = await grantPortalClientAdmin(clientId, {
        email: v.email,
        fullName: v.fullName,
        role: v.role,
        ...(v.role === "admin" && v.scope !== undefined ? { scope: v.scope } : {}),
        grantMatchingAccess: v.grantMatching,
      });
      setGrantOpen(false);
      setNotice({
        kind: res.mailSent ? "ok" : "err",
        text: res.matchingAccess
          ? C.admins.matchingResult(
              res.matchingAccess.setIds.length,
              res.matchingAccess.skippedSetIds.length,
            ) +
            ((res.matchingAccess.materializedSetIds?.length ?? 0) > 0
              ? ` ${C.drawer.materializedNote}`
              : "")
          : res.mailSent
            ? C.admins.assigned
            : K.modal.mailWarning,
      });
    }
    await onChanged();
  }

  async function handleRemove(): Promise<void> {
    if (!removeRow) return;
    setRemoving(true);
    try {
      await revokePortalClientAdmin(clientId, removeRow.userId);
      setRemoveRow(null);
      await onChanged();
    } catch {
      setRemoveRow(null);
      setNotice({ kind: "err", text: C.admins.failed });
    } finally {
      setRemoving(false);
    }
  }

  return (
    <section
      data-testid="client-admins-panel"
      className="rounded-xl"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-start justify-between gap-3 p-4 flex-wrap">
        <p className="text-sm" style={{ color: "var(--color-text-2)", maxWidth: 720 }}>
          {C.admins.subtitle}
        </p>
        <button
          type="button"
          onClick={() => setGrantOpen(true)}
          className="px-4 py-2 rounded-lg text-sm"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            fontWeight: 600,
          }}
        >
          {C.admins.assign}
        </button>
      </div>
      {notice ? (
        <div
          role={notice.kind === "err" ? "alert" : "status"}
          className="mx-4 mb-3 rounded-md px-3 py-2 text-sm"
          style={{
            background:
              notice.kind === "err"
                ? "color-mix(in srgb, var(--color-error) 10%, white)"
                : "color-mix(in srgb, var(--color-success) 12%, white)",
            color: notice.kind === "err" ? "var(--color-error)" : "var(--color-success)",
          }}
        >
          {notice.text}
        </div>
      ) : null}
      {loadError ? (
        <div className="px-4 pb-4 text-sm" style={{ color: "var(--color-error)" }}>
          {C.admins.loadError}
        </div>
      ) : admins === null ? (
        <div className="px-4 pb-4 text-sm" style={{ color: "var(--color-text-3)" }}>
          {C.loading}
        </div>
      ) : admins.length === 0 ? (
        <div className="px-4 pb-4 text-sm" style={{ color: "var(--color-text-3)" }}>
          {C.admins.empty}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="w-full text-sm" style={{ borderCollapse: "collapse", minWidth: 680 }}>
            <thead>
              <tr
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.08em",
                  color: "var(--color-text-3)",
                  textAlign: "left",
                  borderTop: "1px solid var(--color-border)",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                <th className="px-4 py-2 font-semibold">{C.columns.user}</th>
                <th className="px-4 py-2 font-semibold">{C.admins.roleColumn}</th>
                <th className="px-4 py-2 font-semibold">{K.modal.scope}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const parts = scopeSummaryParts(a.scope, sets, entities);
                const last = isLastSuper(a);
                return (
                  <tr
                    key={a.userId}
                    data-testid="client-admin-row"
                    style={{ borderBottom: "1px solid var(--color-border)" }}
                  >
                    <td className="px-4 py-3 align-top">
                      <div style={{ color: "var(--color-text)", fontWeight: 600 }}>
                        {a.fullName}
                        {!a.activated ? (
                          <span
                            className="ml-2 text-xs"
                            style={{ color: "var(--color-warning-text)" }}
                          >
                            {C.admins.pending}
                          </span>
                        ) : !a.isActive ? (
                          <span className="ml-2 text-xs" style={{ color: "var(--color-text-3)" }}>
                            {C.admins.closed}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        {a.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top" style={{ color: "var(--color-text-2)" }}>
                      {CLIENT_ADMIN_ROLE_LABELS[a.role]}
                    </td>
                    <td className="px-4 py-3 align-top" style={{ color: "var(--color-text-2)" }}>
                      {a.role === "super_admin" ? K.fullScope : `${parts.cats} · ${parts.ents}`}
                    </td>
                    <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditRow(a)}
                        className="text-sm mr-3"
                        style={{ color: "var(--color-primary)", fontWeight: 600, minHeight: 36 }}
                      >
                        {C.admins.edit}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveRow(a)}
                        disabled={last}
                        title={last ? C.admins.lastSuper : undefined}
                        className="text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ color: "var(--color-error)", fontWeight: 600, minHeight: 36 }}
                      >
                        {C.admins.remove}
                      </button>
                      {last ? (
                        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                          {C.admins.lastSuper}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AdminGrantModal
        open={grantOpen || editRow !== null}
        row={editRow ?? undefined}
        sets={sets}
        entities={entities}
        scopeStatus={props.scopeStatus}
        onRetryScope={props.onRetryScope}
        warnLastSuper={false}
        defaultRole="admin"
        onClose={() => {
          setGrantOpen(false);
          setEditRow(null);
        }}
        onSubmit={handleSubmit}
      />
      <ConfirmDialog
        open={removeRow !== null}
        title={K.removeDialog.title}
        body={C.admins.removeBody(removeRow?.fullName ?? "")}
        confirmLabel={removing ? K.removing : K.removeDialog.confirm}
        tone="danger"
        busy={removing}
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoveRow(null)}
      />
    </section>
  );
}
